/**
 * Live per-file state strip for an active analyzer session.
 * Renders a scrollable list of every file's current phase (queued,
 * loading_preview, waiting_preview, analyzing, done, error, ...) with
 * timestamps, so admins can see EXACTLY where a session is spending
 * its time.  Polls GET /api/sessions/{id} every 2 s while mounted.
 *
 * Added Phase 44 in response to user report: "Please show details of
 * process so we can identify where it is stalling."
 */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_META = {
  queued:               { label: "Queued",         icon: Clock,        color: "text-slate-400" },
  starting:             { label: "Starting",       icon: Loader2,      color: "text-blue-500", spin: true },
  loading_preview:      { label: "Loading preview",icon: Loader2,      color: "text-emerald-600", spin: true },
  waiting_preview:      { label: "Waiting preview",icon: Loader2,      color: "text-violet-600", spin: true },
  queued_for_analysis:  { label: "Queued for analyze",icon: Clock,     color: "text-slate-500" },
  analyzing:            { label: "Analyzing",      icon: Loader2,      color: "text-amber-600", spin: true },
  done:                 { label: "Done",           icon: CheckCircle2, color: "text-emerald-600" },
  error:                { label: "Error",          icon: AlertTriangle,color: "text-red-600" },
  cancelled:            { label: "Cancelled",      icon: AlertTriangle,color: "text-slate-500" },
  skipped:              { label: "Skipped",        icon: AlertTriangle,color: "text-slate-500" },
};

export default function SessionDetails({ sessionId, isActive }) {
  const [details, setDetails] = useState(null);
  const [err, setErr] = useState(null);
  // Phase 59: user-facing filter + sort so admins can drill into
  // "just the errors" or "just the skipped" without scrolling a giant
  // mixed list. Sort is A→Z by filename which matches how photographers
  // think about a shoot ("go find IMG_0192 in the tape").
  const [phaseFilter, setPhaseFilter] = useState("all");  // all | error | skipped | done

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        // Lightweight endpoint — returns only status + file_states +
        // queue depth. Skips the multi-MB photos + events arrays that
        // the full ``GET /sessions/{id}`` ships, which was tripping
        // Cloudflare's response-size / streaming limits with a 520
        // on long-running sessions.
        const { data } = await axios.get(`${API}/sessions/${sessionId}/live`);
        if (!cancelled) {
          setDetails(data);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message);
      }
      // Continue polling only while the session is still active.
      if (!cancelled && isActive) {
        timer = setTimeout(tick, 2000);
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, isActive]);

  // Phase 57: only count/display ACTUAL images. Companion files
  // (previews, XMP sidecars, DS_Store) never analyse in the first
  // place, so lumping them into "skipped" made the pill misleading.
  const isImageState = (fs) => {
    const name = String(fs?.filename || "").toLowerCase();
    if (!name) return false;
    if (name.endsWith(".preview.jpg")) return false;
    if (name.endsWith(".xmp") || name.endsWith(".txt")
        || name.endsWith(".log") || name.endsWith(".ds_store")) return false;
    return true;
  };

  // Bucket file states by phase for a summary line + per-phase colours.
  const summary = useMemo(() => {
    const buckets = {};
    for (const fs of details?.file_states || []) {
      if (!isImageState(fs)) continue;
      const p = fs.phase || "queued";
      buckets[p] = (buckets[p] || 0) + 1;
    }
    return buckets;
  }, [details]);

  if (err && !details) {
    return (
      <p className="mt-2 text-[11px] text-red-600 font-mono">
        Details unavailable: {err}
      </p>
    );
  }
  if (!details) {
    return (
      <p className="mt-2 text-[11px] text-slate-400 italic">Loading details…</p>
    );
  }
  const states = (details.file_states || []).filter(isImageState);

  // Apply user filter (Phase 59). ``skipped`` covers both the
  // ``skipped`` phase AND ``cancelled`` (they read the same to admins).
  const filteredStates = states.filter((fs) => {
    if (phaseFilter === "all") return true;
    if (phaseFilter === "error") return fs.phase === "error";
    if (phaseFilter === "skipped") {
      return fs.phase === "skipped" || fs.phase === "cancelled";
    }
    if (phaseFilter === "done") return fs.phase === "done";
    return true;
  });
  // Sort alphabetically by full filename (case-insensitive natural
  // order). Photographers naturally scan burst sequences by number,
  // so IMG_0005 → IMG_0006 → IMG_0007 lands right.
  const sortedStates = [...filteredStates].sort((a, b) => {
    const an = String(a.filename || "").toLowerCase();
    const bn = String(b.filename || "").toLowerCase();
    return an.localeCompare(bn, undefined, { numeric: true, sensitivity: "base" });
  });
  return (
    <div className="mt-3 border-t border-slate-100 pt-3" data-testid={`session-details-${sessionId}`}>
      {err && details && (
        <p className="mb-2 text-[10px] text-amber-700 italic bg-amber-50 border border-amber-200 rounded px-2 py-1">
          Last refresh failed ({err.substring(0, 100)}…) — showing cached data, will retry in 2 s.
        </p>
      )}
      <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-2 text-[10px] font-mono">
        {Object.entries(summary).map(([phase, n]) => {
          const meta = PHASE_META[phase] || { label: phase, color: "text-slate-500" };
          return (
            <span key={phase} className={`${meta.color} font-semibold`}>
              {meta.label}: {n}
            </span>
          );
        })}
        {states.length === 0 && (
          <span className="text-slate-400 italic">
            No per-file states recorded yet — will populate as workers start.
          </span>
        )}
        <a
          href={`${API}/sessions/${sessionId}/log`}
          download
          className="ml-auto text-[10px] text-blue-600 hover:text-blue-800 font-semibold underline"
          data-testid={`download-session-log-${sessionId}`}
          title="Download the full plain-text log of every phase transition + error — attach to bug reports"
        >
          Download log
        </a>
      </div>

      {/* Phase 59: filter chips so admins can jump straight to just
          the errored / skipped rows without scrolling. Counts always
          reflect the CURRENT ``summary`` snapshot. */}
      {states.length > 0 && (
        <div className="flex items-center gap-1 mb-2 text-[10px] font-mono">
          {[
            { key: "all",     label: `All (${states.length})` },
            { key: "error",   label: `Errors (${summary.error || 0})` },
            { key: "skipped", label: `Skipped (${(summary.skipped || 0) + (summary.cancelled || 0)})` },
            { key: "done",    label: `Done (${summary.done || 0})` },
          ].map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setPhaseFilter(f.key)}
              className={`px-2 py-0.5 rounded border transition-colors ${
                phaseFilter === f.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`session-filter-${f.key}-${sessionId}`}
            >
              {f.label}
            </button>
          ))}
          <span className="ml-auto text-slate-400 italic">
            Sorted A → Z by filename
          </span>
        </div>
      )}
      {sortedStates.length > 0 ? (
        <div
          className="max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 divide-y divide-slate-100"
          data-testid={`session-file-states-${sessionId}`}
        >
          {sortedStates.slice(0, 500).map((fs) => {
            const meta = PHASE_META[fs.phase] || { label: fs.phase, icon: Clock, color: "text-slate-500" };
            const Icon = meta.icon;
            const shortName = (fs.filename || "").split("/").pop();
            return (
              <div
                key={fs.filename}
                className="flex items-center gap-2 px-2 py-1 text-[11px] font-mono"
                data-testid={`file-state-${fs.filename}`}
              >
                <Icon
                  className={`h-3 w-3 shrink-0 ${meta.color} ${meta.spin ? "animate-spin" : ""}`}
                />
                <span className="truncate flex-1" title={fs.filename}>
                  {shortName}
                </span>
                <span className={`${meta.color} font-semibold shrink-0`}>
                  {meta.label}
                </span>
                {fs.message && (
                  <span
                    className="text-slate-500 truncate max-w-[200px]"
                    title={fs.message}
                  >
                    {fs.message}
                  </span>
                )}
              </div>
            );
          })}
          {sortedStates.length > 500 && (
            <div className="px-2 py-1 text-[10px] text-slate-500 italic text-center">
              …and {sortedStates.length - 500} more (only first 500 shown — narrow the filter to see them)
            </div>
          )}
        </div>
      ) : (
        states.length > 0 && (
          <p className="text-[11px] text-slate-400 italic px-1 py-2">
            No files match the &quot;{phaseFilter}&quot; filter.
          </p>
        )
      )}
    </div>
  );
}
